import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function ChangePassword() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.must_change_password) {
      toast.info("Please set a new password to continue.");
    }
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("New passwords do not match.");
      return;
    }
    if (next.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-4 sm:px-6 py-10 sm:py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="p-6 sm:p-8 lg:p-10">
          <p className="overline text-primary mb-3">Account</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Change your password</h1>
          {user?.must_change_password && (
            <p className="text-sm text-primary mt-3 bg-secondary/40 px-3 py-2 rounded-md" data-testid="must-change-notice">
              You're using a temporary password — please set a new one to continue.
            </p>
          )}
          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current">Current password</Label>
              <Input id="current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="h-11" data-testid="cp-current-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next">New password</Label>
              <Input id="next" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} className="h-11" data-testid="cp-new-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm new password</Label>
              <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" data-testid="cp-confirm-input" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="cp-error">{error}</p>}
            <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full" data-testid="cp-submit-btn">
              {submitting ? "Updating..." : "Update password"}
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}
